import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { clearAuthScopedCaches } from '../services/apiClient';

export const AuthContext = createContext(null);

const decodeJwtPayload = (token) => {
    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return null;
        const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = decodeURIComponent(
            atob(normalized)
                .split('')
                .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
                .join('')
        );
        return JSON.parse(decoded);
    } catch (error) {
        return null;
    }
};

const isTokenExpired = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now();
};

const isLikelyUserPayload = (value) => {
    return (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        (typeof value.id === 'number' || typeof value.id === 'string') &&
        (typeof value.role === 'string' || typeof value.is_superuser === 'boolean')
    );
};

const readStoredSession = () => {
    if (typeof window === 'undefined') {
        return { user: null, token: null, sessionExpired: false };
    }
    const storedToken = localStorage.getItem('token');
    // Cypress uses `cy.clock()` to freeze Date.now() for determinism.
    // When Date is frozen forward, a normally-valid JWT may look "expired"
    // during the initial session read (before AuthProvider's Cypress branch runs),
    // which can incorrectly clear auth state and redirect to `/login`.
    // Skip expiry evaluation during Cypress runs; token refresh + server auth
    // errors are still handled by the normal API client flow.
    const shouldSkipExpiryCheck = Boolean(window.Cypress);
    const tokenExpired = storedToken
        ? (shouldSkipExpiryCheck ? false : isTokenExpired(storedToken))
        : false;
    if (!storedToken || tokenExpired) {
        if (storedToken) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
        }
        return { user: null, token: null, sessionExpired: Boolean(storedToken) };
    }
    try {
        const savedUser = localStorage.getItem('user');
        const parsedUser = savedUser ? JSON.parse(savedUser) : null;
        return {
            user: isLikelyUserPayload(parsedUser) ? parsedUser : null,
            token: storedToken,
            sessionExpired: false,
        };
    } catch (error) {
        console.warn('Failed to parse stored user session, clearing it:', error);
        localStorage.removeItem('user');
        return { user: null, token: storedToken, sessionExpired: false };
    }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [initialSession] = useState(() => readStoredSession());
    const [user, setUser] = useState(() => initialSession.user);
    const [token, setToken] = useState(() => initialSession.token);
    const [loading, setLoading] = useState(true);
    const [, setIsRefreshing] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(() => initialSession.sessionExpired);
    const refreshPromiseRef = useRef(null);

    const clearAuthState = useCallback(() => {
        clearAuthScopedCaches?.();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            const storedToken = localStorage.getItem('token');
            if (!storedToken) {
                clearAuthState();
                setLoading(false);
                return;
            }
            if (typeof window !== 'undefined' && window.Cypress) {
                setToken(storedToken);
                try {
                    const storedUser = localStorage.getItem('user');
                    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
                    if (isLikelyUserPayload(parsedUser)) {
                        setUser(parsedUser);
                    }
                } catch (error) {
                    console.warn('Failed to restore Cypress user session for tests:', error);
                }
                setLoading(false);
                return;
            }

            // Avoid unnecessary failing auth checks if token is already expired.
            if (isTokenExpired(storedToken)) {
                console.info('Stored access token has expired; clearing auth state before app init.');
                clearAuthState();
                setSessionExpired(true);
                setLoading(false);
                return;
            }

            try {
                // Verify token and get user info using apiClient
                const response = await apiClient.get('users/me/');
                const userData = response.data;
                if (!isLikelyUserPayload(userData)) {
                    console.warn('Auth initialization returned an unexpected users/me payload. Preserving existing cached user session.', userData);
                    return;
                }
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                setToken(storedToken);
            } catch (error) {
                console.error('Token validation error:', error);
                clearAuthState();
                if (error.response?.status === 401) {
                    setSessionExpired(true);
                }
            } finally {
                setLoading(false);
            }
        };

        // Listen for logout events from apiClient
        const handleLogout = () => {
            setUser(null);
            setToken(null);
            setSessionExpired(true);
        };

        window.addEventListener('auth:logout', handleLogout);

        const syncTokenFromRefresh = (event) => {
            const access = event?.detail?.access || localStorage.getItem('token');
            setToken(access || null);
        };
        window.addEventListener('auth:token-refreshed', syncTokenFromRefresh);

        initializeAuth().catch((error) => {
            console.error('Auth initialization failed:', error);
            clearAuthState();
            setSessionExpired(true);
            setLoading(false);
        });

        return () => {
            window.removeEventListener('auth:logout', handleLogout);
            window.removeEventListener('auth:token-refreshed', syncTokenFromRefresh);
        };
    }, [clearAuthState]);

    const login = async (username, password) => {
        setLoading(true);
        try {
            const response = await apiClient.post('auth/token/', { username, password });
            const { access, refresh } = response.data;
            localStorage.setItem('token', access);
            localStorage.setItem('refreshToken', refresh);
            setToken(access);
            clearAuthScopedCaches?.();

            // Fetch user info
            const userResponse = await apiClient.get('users/me/');
            if (!isLikelyUserPayload(userResponse.data)) {
                console.error('Login succeeded but users/me returned an invalid profile payload.', userResponse.data);
                clearAuthState();
                return false;
            }
            setUser(userResponse.data);
            localStorage.setItem('user', JSON.stringify(userResponse.data));
            return true;
        } catch (err) {
            return false;
        } finally {
            setLoading(false);
        }
    };

    const logout = useCallback(() => {
        clearAuthScopedCaches?.();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
    }, []);

    const refreshToken = useCallback(async () => {
        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        const refreshPromise = (async () => {
            setIsRefreshing(true);
            const refresh = localStorage.getItem('refreshToken');
            if (!refresh) {
                throw new Error('No refresh token available');
            }

            const response = await apiClient.post('auth/token/refresh/', {
                refresh,
            });
            const { access } = response.data;
            localStorage.setItem('token', access);
            setToken(access);
            return access;
        })();

        refreshPromiseRef.current = refreshPromise;

        try {
            return await refreshPromise;
        } catch (error) {
            console.error('Token refresh error:', error);
            logout();
            throw error;
        } finally {
            refreshPromiseRef.current = null;
            setIsRefreshing(false);
        }
    }, [logout]);

    const value = {
        user,
        currentUser: user, // Add alias for currentUser
        token,
        loading,
        isAuthenticated: !!user, // Add isAuthenticated boolean
        isClient: user?.role === 'client', // Week 4: Client Portal Foundation
        isInternalUser: user?.role !== 'client', // Week 4: For internal staff users
        login,
        logout,
        refreshToken,
        sessionExpired,
        clearSessionExpired: () => setSessionExpired(false),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 